import {
  DeleteOutlined,
  FormOutlined,
  SaveOutlined,
  ShareAltOutlined,
} from '@ant-design/icons'
import { Button, Input, Modal, Select, Tag, Tooltip } from 'antd'
import CantileverButton from 'components/CantileverButton'
import { useRolodexExplorationsForUser } from 'features/drilldown'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import React, {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'

export type DrilldownExploration = {
  id: string
  name: string
  published: boolean
  organizationId: string | null
  siteId: string | null
  updatedAt: Date
}

export interface DrilldownExplorationPickerProps {
  selectedId: string | undefined
  setSelectedExploration: Dispatch<SetStateAction<string | undefined>>
  addNewItem?: (name: string) => Promise<void>
  unlinkCurrentExploration?: () => void
  allowDelete?: boolean
  allowPublish?: boolean
  onPublish?: (id: string, published: boolean) => void
}

const { Option } = Select

const MenuItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

interface DeleteModalProps {
  deleteId: string | undefined
  deleteName: string
  selectedId: string | undefined
  setSelectedExploration: Dispatch<SetStateAction<string | undefined>>
  refreshData: () => Promise<void>
  setDeleteId: Dispatch<SetStateAction<string | undefined>>
}

const DeleteModal: FC<DeleteModalProps> = ({
  deleteId,
  deleteName,
  selectedId,
  setSelectedExploration,
  refreshData,
  setDeleteId,
}) => {
  const { rolodexDelete } = useRolodexEdit()
  return (
    <Modal
      title={`Delete exploration: ${deleteName}`}
      // TODO (xdowns) Handle delete clicks nicely for non-current explorations
      //  https://app.asana.com/1/957917091101853/project/1209606087561609/task/1210044748563051
      // TODO (xdowns) Get Rolodex Exploration deletion working (again)
      //  https://app.asana.com/1/957917091101853/project/1209606087561609/task/1210044748563055
      onOk={async () => {
        if (!deleteId) {
          return
        }
        if (deleteId === selectedId) {
          setSelectedExploration(undefined)
        }
        setTimeout(async () => {
          await rolodexDelete(deleteId)
          await refreshData()
        }, 500)
        setDeleteId(undefined)
      }}
      onCancel={() => setDeleteId(undefined)}
      open={!!deleteId && deleteId === selectedId}>
      Are you sure you want to delete this exploration?
    </Modal>
  )
}

interface PublishModalProps {
  item: DrilldownExploration
  refreshData: () => Promise<void>
  setPublishId: Dispatch<SetStateAction<string | undefined>>
  onPublish?: (id: string, published: boolean) => void
}

const PublishModal: FC<PublishModalProps> = ({
  item,
  refreshData,
  setPublishId,
  onPublish,
}) => {
  const { rolodexPatch } = useRolodexEdit()
  return (
    <Modal
      title={`Publish ${item.name}?`}
      onOk={async () => {
        await rolodexPatch([{ id: item.id, data: { published: true } }])
        await refreshData()
        onPublish?.(item.id, true)
        setPublishId(undefined)
      }}
      onCancel={() => setPublishId(undefined)}
      open>
      This will make the exploration available in the app selector.
    </Modal>
  )
}

const UnpublishModal: FC<PublishModalProps> = ({
  item,
  refreshData,
  setPublishId,
  onPublish,
}) => {
  const { rolodexPatch } = useRolodexEdit()
  return (
    <Modal
      title={`Unpublish ${item.name}?`}
      onOk={async () => {
        await rolodexPatch([{ id: item.id, data: { published: false } }])
        await refreshData()
        onPublish?.(item.id, false)
        setPublishId(undefined)
      }}
      onCancel={() => setPublishId(undefined)}
      open>
      This will remove the exploration from the app selector.
    </Modal>
  )
}

const PublishingModal: FC<PublishModalProps> = ({ item, ...rest }) => {
  return (
    <>
      {item.published ? (
        <UnpublishModal item={item} {...rest} />
      ) : (
        <PublishModal item={item} {...rest} />
      )}
    </>
  )
}

/**
 * The picker for explorations in Drilldown - appears in the top right of the
 * drilldown app.
 */
const RawDrilldownExplorationPicker: FC<DrilldownExplorationPickerProps> = ({
  addNewItem,
  selectedId,
  setSelectedExploration,
  unlinkCurrentExploration,
  allowDelete,
  allowPublish,
  onPublish,
}) => {
  const [inputValue, setInputValue] = useState<string>('')

  const { explorations, isLoading, forceRefresh } =
    useRolodexExplorationsForUser()

  const addItem = useCallback(async () => {
    if (!addNewItem) {
      return
    }
    await addNewItem(inputValue)
    setInputValue('')
    setTimeout(() => forceRefresh(), 500)
  }, [addNewItem, forceRefresh, inputValue])

  const [deleteId, setDeleteId] = useState<string | undefined>(undefined)
  const deleteItem = useMemo(
    () => explorations.find((e) => e.id === deleteId),
    [deleteId, explorations]
  )

  const [publishId, setPublishId] = useState<string | undefined>(undefined)
  const publishingItem = useMemo(
    () => explorations.find((e) => e.id === publishId),
    [explorations, publishId]
  )

  const explorationsByLastUpdate = useMemo(
    () =>
      [...explorations].sort(
        (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()
      ),
    [explorations]
  )
  return (
    <>
      {allowDelete && (
        <DeleteModal
          {...{ deleteId, setDeleteId, selectedId, setSelectedExploration }}
          refreshData={forceRefresh}
          deleteName={deleteItem?.name ?? 'Unknown'}
        />
      )}
      {!!publishingItem && (
        <PublishingModal
          item={publishingItem}
          refreshData={forceRefresh}
          setPublishId={setPublishId}
          onPublish={onPublish}
        />
      )}
      <Select
        style={{ width: 300 }}
        loading={isLoading}
        placeholder='Saved Explorations'
        onChange={setSelectedExploration}
        value={selectedId}
        labelRender={() =>
          explorations.find((e) => e.id === selectedId)?.name ?? ''
        }
        dropdownRender={(menu) => (
          <>
            {menu}
            {!!addNewItem && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '8px',
                }}>
                <Input
                  placeholder='Save as...'
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onPressEnter={(e: React.KeyboardEvent) => {
                    e.stopPropagation()
                    addItem()
                  }}
                  style={{ marginRight: '8px', flex: 'auto' }}
                />
                <Button
                  type='text'
                  onClick={addItem}
                  disabled={!inputValue.length}>
                  <SaveOutlined />
                </Button>
              </div>
            )}
          </>
        )}>
        {explorationsByLastUpdate.map((item) => (
          <Option key={item.id} value={item.id}>
            <MenuItemContainer>
              {item.name}{' '}
              <div>
                {!!allowPublish && (
                  <Tag.CheckableTag
                    checked={item.published}
                    onChange={() => setPublishId(item.id)}>
                    <ShareAltOutlined />
                  </Tag.CheckableTag>
                )}
                {!!allowDelete && (
                  <Button
                    type='text'
                    disabled={!item.id}
                    onClick={() => setDeleteId(item.id)}>
                    <DeleteOutlined style={{ color: 'red' }} />
                  </Button>
                )}
              </div>
            </MenuItemContainer>
          </Option>
        ))}
      </Select>
      {unlinkCurrentExploration && (
        <Tooltip title='Open a new investigation'>
          <CantileverButton
            iconOnly
            onClick={() => unlinkCurrentExploration()}
            title='Open a new investigation'>
            <FormOutlined />
          </CantileverButton>
        </Tooltip>
      )}
    </>
  )
}

export const DrilldownExplorationPicker = React.memo(
  RawDrilldownExplorationPicker
)
